import { useQuery, useQueries, useMutation, UseQueryResult } from '@tanstack/react-query';
import type { AxiosProgressEvent } from 'axios';
import api from '../lib/api';
import {
  UploadParams,
  UploadResult,
  UploadResponse,
  DeleteFileParams,
  DeleteFileResponse,
  SignedUrlParams,
  SignedUrlResult,
  SignedUrlResponse,
} from '../types/upload';

// ─── Upload ─────────────────────────────────────────────────────────────

export interface UploadOptions {
  onUploadProgress?: (event: AxiosProgressEvent) => void;
}

/**
 * Upload a file to tenant-isolated S3 storage
 * POST /api/v1/uploads  (multipart/form-data)
 */
export const uploadFile = async (
  params: UploadParams,
  options?: UploadOptions
): Promise<UploadResult> => {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('resource_type', params.resource_type);
  formData.append('resource_id', params.resource_id);

  if (params.section_id) {
    formData.append('section_id', params.section_id);
  }

  const response = await api.post<UploadResponse>('/uploads', formData, {
    // Large files (SCORM zips, videos) legitimately exceed the global 10s
    // API timeout — a mid-flight client abort strands a successful server
    // upload as __pending__. Three minutes covers 500MB on slow links.
    timeout: 180_000,
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: options?.onUploadProgress,
  });
  return response.data.data;
};

export const useUploadFile = () => {
  return useMutation({
    mutationFn: (params: UploadParams) => uploadFile(params),
  });
};

// ─── Delete ─────────────────────────────────────────────────────────────

/**
 * Delete a file from S3 storage
 * DELETE /api/v1/uploads
 */
export const deleteFile = async (params: DeleteFileParams): Promise<DeleteFileResponse> => {
  const response = await api.delete<{ data: DeleteFileResponse }>('/uploads', {
    data: { key: params.key },
  });
  return response.data.data;
};

export const useDeleteFile = () => {
  return useMutation({
    mutationFn: deleteFile,
  });
};

// ─── Signed URL ─────────────────────────────────────────────────────────

/**
 * Get a presigned URL for an uploaded file
 * GET /api/v1/uploads/signed-url
 */
export const getSignedUrl = async (params: SignedUrlParams): Promise<SignedUrlResult> => {
  const queryParams = new URLSearchParams();
  queryParams.append('key', params.key);

  if (params.expires_in !== undefined) {
    queryParams.append('expires_in', params.expires_in.toString());
  }

  const response = await api.get<SignedUrlResponse>(
    `/uploads/signed-url?${queryParams.toString()}`
  );
  return response.data.data;
};

/**
 * Hook to fetch a presigned URL for a given S3 key.
 * Automatically disabled when key is falsy.
 */
export const useSignedUrl = (
  key: string | undefined | null,
  expiresIn?: number
): UseQueryResult<SignedUrlResult, Error> => {
  return useQuery({
    queryKey: ['signed-url', key, expiresIn],
    queryFn: () => getSignedUrl({ key: key!, expires_in: expiresIn }),
    enabled: !!key,
    staleTime: 30 * 60 * 1000, // 30 min (URL valid for 1h by default)
    gcTime: 45 * 60 * 1000,
  });
};

/**
 * Resolves an image value to a displayable URL.
 * If the value is already a full URL (http/https), returns it as-is.
 * If it's an S3 key (path), fetches a presigned URL.
 *
 * Returns [resolvedUrl, isLoading] — isLoading is true while a
 * presigned URL is being fetched for an S3 key.
 */
export function useImageUrl(value: string | null | undefined): [string | null, boolean] {
  const isKey = !!value && !value.startsWith('http');
  const { data, isLoading } = useSignedUrl(isKey ? value : null);

  if (!value) return [null, false];
  if (isKey) return [data?.url ?? null, isLoading];
  return [value, false];
}

/**
 * Resolves an array of image values to displayable URLs.
 * Each value is resolved independently — S3 keys get presigned URLs,
 * full URLs are passed through as-is. Maintains order.
 */
export function useImageUrls(values: string[]): string[] {
  const keys = values.filter((v) => !v.startsWith('http'));
  const queries = useQueries({
    queries: keys.map((key) => ({
      queryKey: ['signed-url', key] as const,
      queryFn: () => getSignedUrl({ key }),
      enabled: true,
      staleTime: 30 * 60 * 1000,
      gcTime: 45 * 60 * 1000,
    })),
  });

  const keyToUrl = new Map<string, string>();
  keys.forEach((key, i) => {
    const url = queries[i]?.data?.url;
    if (url) keyToUrl.set(key, url);
  });

  return values.map((v) => {
    if (v.startsWith('http')) return v;
    return keyToUrl.get(v) ?? v;
  });
}
